import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Collapse,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import {
  getFileCategory,
  formatFileSize,
  formatDateWithTime,
  getFolderSize,
} from '../../../../common/utils/fileUtils';
import {
  getFileIcon,
  getFileTypeDisplay,
} from '@/common/utils/fileDisplayUtils';
import { buildFileTree, type FileNode } from '@/common/utils/fileTreeUtils';
import { useFileModificationTimes } from '../hooks/useFileModificationTimes';
import { AudioPlayer } from '../components/AudioPlayer';
import { FileTreeItemActionsMenu } from './FileTreeItemActionsMenu';
import { FileCategory } from '@/common/models/enums';
import { WaveForm } from '@/generated/graphql';
import MobileFileExplorer from './MobileFileExplorer';

// ─── Column widths (desktop) ─────────────────────────────────────────────────
const COL_PREVIEW = 200;
const COL_DATE = 140;
const COL_SIZE = 70;
const COL_KIND = 90;
const COL_ACTIONS = 24;

const actionsColumnSx = {
  width: COL_ACTIONS,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

interface FileTreeViewProps {
  files: Array<{
    fileName: string;
    size: number;
    contentType: string;
    uploadedAt: string;
    _id: string;
    quickHash: string;
    version: number;
  }>;
  projectDirPath?: string | null;
  projectId: string;
}

const FileTreeView: React.FC<FileTreeViewProps> = ({
  files,
  projectDirPath,
  projectId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set()
  );

  // Filter out sidecar/analysis files (e.g. Ableton .asd files)
  const filteredFiles = useMemo(
    () => files.filter((f) => !f.fileName.endsWith('.asd')),
    [files]
  );

  // Use hook to fetch file modification times
  const fileModTimes = useFileModificationTimes(filteredFiles, projectDirPath);

  // Build file tree using utility function
  const fileTree = useMemo(() => buildFileTree(filteredFiles), [filteredFiles]);

  const toggleFolder = useCallback((path: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  // Renders a tree node for the desktop Finder layout.
  // `counter` is a mutable object shared across the recursive call so file rows
  // get a stable sequential index for alternating stripe colors.
  const renderTreeNode = (
    node: FileNode,
    depth: number,
    counter: { n: number }
  ): React.ReactNode => {
    const isExpanded = expandedFolders.has(node.path);
    // 8px base + 16px per depth level
    const pl = 8 + depth * 16;

    if (node.type === 'folder') {
      return (
        <React.Fragment key={node.path}>
          {/* Folder row — acts as a visual group divider */}
          <Box
            data-cy={`folder-name-${node.name}`}
            onClick={() => toggleFolder(node.path)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 24,
              pl: `${pl}px`,
              pr: 1,
              cursor: 'pointer',
              backgroundColor: alpha(theme.palette.common.white, 0.04),
              borderTop: `1px solid ${theme.palette.divider}`,
              borderBottom: `1px solid ${theme.palette.divider}`,
              userSelect: 'none',
              '&:hover': {
                backgroundColor: alpha(theme.palette.common.white, 0.07),
              },
            }}
          >
            {/* Chevron */}
            <Box
              sx={{
                width: 14,
                mr: 0.5,
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              {isExpanded ? (
                <ExpandMoreIcon
                  sx={{ fontSize: 12, color: 'text.secondary' }}
                />
              ) : (
                <ChevronRightIcon
                  sx={{ fontSize: 12, color: 'text.secondary' }}
                />
              )}
            </Box>
            {/* Folder icon */}
            <Box
              sx={{
                width: 16,
                mr: 0.75,
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              {isExpanded ? (
                <FolderOpenIcon sx={{ color: 'primary.main', fontSize: 14 }} />
              ) : (
                <FolderIcon sx={{ color: 'primary.main', fontSize: 14 }} />
              )}
            </Box>
            {/* Name */}
            <Typography
              sx={{
                flex: 1,
                fontSize: '12px',
                fontWeight: 500,
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {node.name}
            </Typography>
            {/* Column spacers */}
            <Box sx={{ width: COL_PREVIEW, flexShrink: 0 }} />
            <Box sx={{ width: COL_DATE, flexShrink: 0 }} />
            <Box
              sx={{
                width: COL_SIZE,
                flexShrink: 0,
                textAlign: 'right',
                pr: 1,
              }}
            >
              <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                {formatFileSize(getFolderSize(node))}
              </Typography>
            </Box>
            <Box sx={{ width: COL_KIND, flexShrink: 0, pl: 1 }}>
              <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                Folder
              </Typography>
            </Box>
            <Box
              sx={actionsColumnSx}
              onClick={(event) => event.stopPropagation()}
            >
              <FileTreeItemActionsMenu
                node={node}
                projectDirPath={projectDirPath}
              />
            </Box>
          </Box>

          <Collapse in={isExpanded} timeout={150} unmountOnExit>
            {node.children?.map((child) =>
              renderTreeNode(child, depth + 1, counter)
            )}
          </Collapse>
        </React.Fragment>
      );
    }

    // ── File row ──────────────────────────────────────────────────────────────
    const fileIdx = counter.n++;
    const isAudio =
      getFileCategory(node.contentType || '', node.name || '') ===
        FileCategory.AUDIO && !!node.waveForm;

    return (
      <Box
        data-cy={`file-name-${node.name}`}
        key={node.path}
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: isAudio ? 48 : 22,
          pl: `${pl}px`,
          pr: 1,
          backgroundColor:
            fileIdx % 2 === 1
              ? alpha(theme.palette.common.white, 0.025)
              : 'transparent',
          '&:hover': {
            backgroundColor: alpha(theme.palette.common.white, 0.07),
          },
        }}
      >
        {/* Name */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* chevron-width spacer for alignment with folders */}
          <Box sx={{ width: 14, mr: 0.5, flexShrink: 0 }} />
          <Box
            sx={{
              width: 16,
              mr: 0.75,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {getFileIcon(node.name, node.contentType || '')}
          </Box>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 400,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {node.name}
          </Typography>
        </Box>

        {/* Preview */}
        <Box sx={{ width: COL_PREVIEW, flexShrink: 0, pr: 1 }}>
          {isAudio && (
            <AudioPlayer
              waveForm={node.waveForm as WaveForm}
              projectId={projectId}
              fileId={node._id}
            />
          )}
        </Box>

        {/* Date Modified */}
        <Box sx={{ width: COL_DATE, flexShrink: 0, pr: 1 }}>
          <Typography
            sx={{
              fontSize: '11px',
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {formatDateWithTime(
              fileModTimes.get(node.path) || node.uploadedAt || ''
            )}
          </Typography>
        </Box>

        {/* Size */}
        <Box sx={{ width: COL_SIZE, flexShrink: 0, textAlign: 'right', pr: 1 }}>
          <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
            {formatFileSize(node.size || 0)}
          </Typography>
        </Box>

        {/* Kind */}
        <Box sx={{ width: COL_KIND, flexShrink: 0, pl: 1 }}>
          <Typography
            sx={{
              fontSize: '11px',
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {getFileTypeDisplay(node.name, node.contentType || '')}
          </Typography>
        </Box>
        <Box sx={actionsColumnSx} onClick={(event) => event.stopPropagation()}>
          <FileTreeItemActionsMenu
            node={node}
            projectDirPath={projectDirPath}
          />
        </Box>
      </Box>
    );
  };

  if (fileTree.length === 0) {
    return null;
  }

  // ── Mobile layout ────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <MobileFileExplorer
        fileTree={fileTree}
        projectId={projectId}
        expandedFolders={expandedFolders}
        toggleFolder={toggleFolder}
      />
    );
  }

  // ── Desktop — macOS Finder style ─────────────────────────────────────────────
  const counter = { n: 0 };
  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        overflow: 'clip',
        overflowX: 'auto',
      }}
    >
      {/* Column headers */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 26,
          pl: '40px', // aligns with name text (8 base + 14 chevron + 4 gap + 16 icon gap)
          pr: 1,
          backgroundColor: alpha(theme.palette.common.white, 0.04),
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{ fontSize: '11px', color: 'text.secondary', fontWeight: 500 }}
          >
            Name
          </Typography>
        </Box>
        <Box sx={{ width: COL_PREVIEW, flexShrink: 0 }}>
          <Typography
            sx={{ fontSize: '11px', color: 'text.secondary', fontWeight: 500 }}
          >
            Preview
          </Typography>
        </Box>
        <Box sx={{ width: COL_DATE, flexShrink: 0, pr: 1 }}>
          <Typography
            sx={{ fontSize: '11px', color: 'text.secondary', fontWeight: 500 }}
          >
            Date Modified
          </Typography>
        </Box>
        <Box
          sx={{
            width: COL_SIZE,
            flexShrink: 0,
            textAlign: 'right',
            pr: 1,
          }}
        >
          <Typography
            sx={{ fontSize: '11px', color: 'text.secondary', fontWeight: 500 }}
          >
            Size
          </Typography>
        </Box>
        <Box sx={{ width: COL_KIND, flexShrink: 0, pl: 1 }}>
          <Typography
            sx={{ fontSize: '11px', color: 'text.secondary', fontWeight: 500 }}
          >
            Kind
          </Typography>
        </Box>
        <Box sx={{ width: COL_ACTIONS, flexShrink: 0 }} />
      </Box>

      {/* Tree rows */}
      {fileTree.map((node) => renderTreeNode(node, 0, counter))}
    </Box>
  );
};

export default FileTreeView;
